/**
 * GalleryAddToProductBridge — flag-gated entry point for assigning gallery
 * images onto a product (single-image detail case + multi-select).
 *
 * Flag ON  → new unified AddToProductOverlay (no `texts` → overlay opens
 *            directly in the images view, tab bar hidden). Supports N images.
 * Flag OFF → legacy TransferToProductDialog (1:1 byte-identical behaviour
 *            vs. the pre-PR ImageDetailModal call). The legacy dialog only
 *            supports ONE image; for the single-image detail case we pass the
 *            original `legacyImage` through unchanged. For any non-single
 *            selection the legacy path renders nothing (multi-select is only
 *            offered when the flag is ON). The legacy import is intentionally
 *            kept — PR7 removes it once the flag is rolled out.
 *
 * Default: flag OFF → zero user-visible change.
 */

import { useTranslation } from 'react-i18next'
import { AddToProductOverlay } from '@/components/v2/product/add-to-product'
import { useUnifiedAddToProductFlag } from '@/hooks/useUnifiedAddToProductFlag'
import { TransferToProductDialog } from './TransferToProductDialog'
import type { Image } from '@/types'

export interface GalleryAddToProductBridgeProps {
  open: boolean
  imageIds: string[]
  /** Original gallery Image for the single-image detail case (OFF path 1:1). */
  legacyImage?: Image
  onClose: () => void
  onComplete?: () => void
}

export function GalleryAddToProductBridge({
  open,
  imageIds,
  legacyImage,
  onClose,
  onComplete,
}: GalleryAddToProductBridgeProps) {
  const { t } = useTranslation()
  const flagEnabled = useUnifiedAddToProductFlag()

  if (!open) return null

  if (flagEnabled) {
    return (
      <AddToProductOverlay
        payload={{
          source: 'gallery',
          title: t('addToProduct.buttonOpen'),
          images: imageIds.map((id) => ({
            id,
            url: `/thumb/${id}`,
            kind: 'asset' as const,
          })),
        }}
        onClose={onClose}
        onComplete={onComplete}
      />
    )
  }

  // Legacy dialog supports exactly ONE image. Single-image detail case →
  // pass the original Image through unchanged so OFF stays 1:1. Multi-select
  // has no legacy path (only offered when flag ON) → render nothing.
  const legacy =
    legacyImage ?? (imageIds.length === 1 ? ({ id: imageIds[0] } as Image) : null)
  if (!legacy) return null

  return (
    <TransferToProductDialog
      show={open}
      image={legacy}
      onClose={onClose}
      onSuccess={onComplete}
    />
  )
}
